import React, { useEffect, useState, useCallback } from "react";
import { WifiOff, AlertTriangle, RefreshCw, X } from "lucide-react";

/**
 * ConnectivityBanner — a global top-of-app strip that appears whenever an
 * auto-save fails. Listens for `app:save-failed` and `app:save-ok`
 * CustomEvents dispatched by `useUnsavedGuard`, plus the browser's native
 * `online` / `offline` events so the coach knows *why* their edit didn't
 * stick even if the form modal is closed.
 *
 * Behaviour:
 *   - Offline detected → red banner with a "You are offline" message
 *   - Save failed (online) → amber banner with a "Save failed" message + Retry
 *   - `app:save-ok` clears the banner
 *   - Also polls navigator.onLine every 5s so we catch cases where the OS
 *     silently misses the online event (common on mobile WiFi transitions).
 */
export default function ConnectivityBanner() {
  const [state, setState] = useState(null);  // null | {kind:"offline"|"failed", reason?, retry?}

  useEffect(() => {
    const onFail = (e) => {
      setState({
        kind: e.detail?.offline ? "offline" : "failed",
        reason: e.detail?.reason,
        retry: e.detail?.retry,
      });
    };
    const onOk = () => setState(null);
    const onOffline = () => setState({ kind: "offline" });
    const onOnline = () => setState((s) => (s?.kind === "offline" ? null : s));
    window.addEventListener("app:save-failed", onFail);
    window.addEventListener("app:save-ok", onOk);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    // Polling fallback for mobile-network flakiness.
    const iv = setInterval(() => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setState((s) => s || { kind: "offline" });
      }
    }, 5000);
    return () => {
      window.removeEventListener("app:save-failed", onFail);
      window.removeEventListener("app:save-ok", onOk);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      clearInterval(iv);
    };
  }, []);

  const handleRetry = useCallback(async () => {
    if (!state?.retry) return;
    try {
      await state.retry();
    } catch {
      /* retry itself will re-dispatch save-failed if it fails again */
    }
  }, [state]);

  if (!state) return null;

  const isOffline = state.kind === "offline";

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[500] w-full max-w-lg mx-auto px-4`}
      data-testid="connectivity-banner"
      role="status"
    >
      <div
        className={`card !p-3 flex items-center gap-3 ${
          isOffline
            ? "!bg-rose-500/15 !border-rose-500/40"
            : "!bg-amber-500/15 !border-amber-500/40"
        }`}
      >
        {isOffline ? (
          <WifiOff size={16} className="text-rose-300 shrink-0" />
        ) : (
          <AlertTriangle size={16} className="text-amber-300 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${isOffline ? "text-rose-100" : "text-amber-100"}`}>
            {isOffline
              ? "You appear to be offline."
              : "Auto-save failed."}
          </div>
          <div className={`text-[11px] mt-0.5 ${isOffline ? "text-rose-300" : "text-amber-300"} truncate`}>
            {isOffline
              ? "Changes will be re-tried when you reconnect. Keep this tab open."
              : (state.reason || "Please try again.")}
          </div>
        </div>
        {state.retry && !isOffline && (
          <button
            onClick={handleRetry}
            className="pill !text-[11px] !py-1 !px-2"
            data-testid="connectivity-retry-btn"
          >
            <RefreshCw size={11} /> Retry
          </button>
        )}
        <button
          onClick={() => setState(null)}
          className="p-1 rounded hover:bg-black/20 text-slate-300"
          data-testid="connectivity-dismiss-btn"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
